import { useEffect, useState } from "react";

// react-router-dom components
import { Link, useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import { Alert, Snackbar } from "@mui/material";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React context
import { useMaterialUIController, setLayout } from "context";

// Custom Components
import Timer from "examples/modal/Timer";
import AccessDenied from "components/AccessDenied/accessDenied";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { closeSnackBar } from "redux/Slice/Notification";
import configThunk from "redux/Thunks/Config";
import { resetAllFilters } from "redux/Slice/Filter";
import { resetStateThunk } from "redux/Thunks/Authentication";

// Utils
import Session from "utils/Sessions";
import Constants, { defaultData, ModalContent, Icons, Colors } from "utils/Constants";
import { identifyNotificationRoute } from "utils/methods/methods";

// 3rd party
import jwtDecode from "jwt-decode";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Firebase for notifications
import { generateToken, messaging } from "notification/firebase";
import { onMessage } from "firebase/messaging";

function DashboardLayout({ xPadding, children, module }) {
  const [logout, setLogout] = useState(false);
  const [logoutReason, setLogoutReason] = useState(null);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const Notification = useSelector((state) => state.Notification);
  const { config, loading } = useSelector((state) => state.config);
  const [isRender, setIsRender] = useState(null);
  const [customSnackbar, setCustomSnackbar] = useState({
    open: false,
    message: "",
    description: "",
  });

  const dispatchAction = useDispatch();
  useEffect(() => {
    if (module !== Constants.DEFAULT) {
      const isPermission = config[0]?.screens?.find((screen) => screen.screenId === module)
        ?.agreement?.read;
      setIsRender(isPermission);
    }
    // Only check logout conditions if we have valid config data
    if (config && config.length > 0 && config[0]) {
      // Check for role change
      const previousRole = config[0].role;
      const currentRole = config[0].tokenRole;
      if (previousRole && currentRole && previousRole !== currentRole) {
        setLogoutReason(ModalContent.ON_ROLE_CHANGE);
        setLogout(false);
      } else if (config[0].isActive === false) {
        // Check if user is inactive
        setLogoutReason(ModalContent.INACTIVE_USER);
        setLogout(false);
      } else if (config[0].isRoleActive === false) {
        // Check if role is inactive
        setLogoutReason(ModalContent.INACTIVE_ROLE);
        setLogout(false);
      } else if (config[0].accessType === defaultData.MOBILE_ACCESSTYPE) {
        // Check if access type is mobile only
        setLogoutReason(ModalContent.MOBILE_ONLY_ROLE);
        setLogout(false);
      }
    }
  }, [config]);

  // Check for force logout from ApiService
  const checkForceLogout = () => {
    const forceLogoutFlag = localStorage.getItem("forceLogout");
    const logoutMessage = localStorage.getItem("logoutMessage");

    if (forceLogoutFlag === "true" && logoutMessage) {
      // Clear the flags immediately to prevent repeated triggers
      setLogout(true);
      setLogoutReason(logoutMessage);
      localStorage.removeItem("forceLogout");
      localStorage.removeItem("logoutMessage");
    }
  };

  // Check on route changes
  useEffect(() => {
    checkForceLogout();
  }, [pathname]);

  // Also check on component mount and at intervals
  useEffect(() => {
    checkForceLogout();

    // Set up an interval to check periodically (every 1 second)
    const intervalId = setInterval(checkForceLogout, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    (async () => {
      setLayout(dispatch, "dashboard");
      const token = jwtDecode(Session.userToken);
      if (
        config?.length === 0 &&
        (token.role !== defaultData.SUPER_ADMIN_ROLE ||
          (token.role === defaultData.SUPER_ADMIN_ROLE && Session.isSuperAdminViewingAdminPanel))
      )
        await dispatchAction(configThunk());
    })();
  }, [pathname]);

  const handleSnackbarClose = () => {
    dispatchAction(closeSnackBar());
  };

  const handleCustomSnackbarClose = () => {
    setCustomSnackbar({ open: false, message: "", description: "" });
  };

  const handleLogout = async () => {
    Session.setClear();
    navigate("/authentication/sign-in", { replace: true });
    await dispatchAction(resetStateThunk());
    await dispatchAction(resetAllFilters());

    // Reset logout modal state
    setLogout(false);
    setLogoutReason(null);
  };

  const getNotificationBackgroundColor = () => {
    if (Notification.notificationType === "success") {
      return "green";
    }

    if (Notification.notificationType === "info") {
      return Colors.PRIMARY;
    }

    return "error";
  };

  const renderContent = () => {
    if (loading === Constants.PENDING) {
      // While data is loading, render a placeholder or spinner
      return (
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {Icons.LOADING2}
        </MDBox>
      );
    }

    if (loading === Constants.FULFILLED2) {
      // Only evaluate isRender when loading is fulfilled
      if (isRender) {
        return children;
      }
      if (isRender === false) {
        return (
          <>
            <DashboardNavbar />
            <AccessDenied />
          </>
        );
      }
    }

    // Handle unexpected states, if necessary
    return null;
  };

  // Notification
  useEffect(() => {
    generateToken();
    onMessage(messaging, (payload) => {
      const route = identifyNotificationRoute(payload?.data?.module, payload?.data?.actionUrl);
      setCustomSnackbar({
        open: true,
        message: payload.notification.title,
        description: payload.notification.body,
        route,
      });
    });
  }, []);
  return (
    <MDBox
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        px: pxToRem(xPadding),
        position: "relative",
        background: "linear-gradient(0deg, #f6f7ff, #f6f7ff)",
        minHeight: "100vh",

        [breakpoints.up("xl")]: {
          marginLeft: miniSidenav ? pxToRem(65) : pxToRem(250),
          transition: transitions.create(["margin-left", "margin-right"], {
            easing: transitions.easing.easeInOut,
            duration: transitions.duration.standard,
          }),
        },
      })}
    >
      {module === Constants.DEFAULT ? children : renderContent()}
      <Link to={customSnackbar.route || "/client/setting"}>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={customSnackbar.open}
          autoHideDuration={5000}
          onClose={handleCustomSnackbarClose}
        >
          <Alert onClose={handleCustomSnackbarClose} severity="success" variant="filled">
            <MDBox display="flex" alignItems="flex-start" flexDirection="column">
              {customSnackbar.message && (
                <MDBox
                  style={{
                    color: Colors.WHITE,
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginRight: "0.5rem",
                  }}
                >
                  {customSnackbar.message}
                </MDBox>
              )}
              {customSnackbar.description && (
                <MDBox
                  style={{
                    color: Colors.WHITE_2,
                    fontSize: "16px",
                    fontStyle: "italic",
                    marginBottom: "0.25rem",
                    textTransform: "capitalize",
                  }}
                >
                  {customSnackbar.description}
                </MDBox>
              )}
            </MDBox>
          </Alert>
        </Snackbar>
      </Link>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={Notification.openSnackbar}
        autoHideDuration={Notification.notificationType === "error" ? 5000 : 3500}
        onClose={handleSnackbarClose}
        sx={{
          bottom: "30px !important",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={Notification.notificationType}
          variant="filled"
          sx={{
            width: "100%",
            backgroundColor: getNotificationBackgroundColor(),
            boxShadow: 3,
            borderRadius: "8px",
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-icon": {
              display: Notification.notificationType === "info" ? "none" : "flex",
            },
            "& .MuiAlert-action": {
              alignItems: "center",
            },
          }}
        >
          {typeof Notification.snackbarMessage === "string" ? (
            <MDTypography
              variant="button"
              color="light"
              fontWeight="regular"
              sx={{ fontSize: "14px" }}
            >
              {Notification.snackbarMessage}
            </MDTypography>
          ) : (
            Notification.snackbarMessage
          )}
        </Alert>
      </Snackbar>

      {/* Logout user when role is changed or other conditions are met */}
      <Timer open={logout} message={logoutReason} callback={handleLogout} />
    </MDBox>
  );
}

DashboardLayout.defaultProps = {
  xPadding: 30,
  // [default,allowed,notAllowed]
  // default props will render the children when prop is not specified or for static pages.
  // module true props will render the children when the user read agreement for the calling module.
  // module false props will render the access denied page.
  module: Constants.DEFAULT,
};

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  xPadding: PropTypes.number,
  module: PropTypes.string,
};

export default DashboardLayout;
